import { useRef, useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useEditorStore } from '../../store/editorStore';
import { mediaApi } from '../../api/client';

export default function AudioProperties({ layer }) {
  const data = layer.data;
  const updateLayerData = useEditorStore((s) => s.updateLayerData);
  const story = useEditorStore((s) => s.story);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  function update(updates) {
    updateLayerData(layer.id, updates);
  }

  async function handleUpload(file) {
    if (!file) return;
    setUploading(true);
    try {
      const asset = await mediaApi.upload(file, story?.id);
      update({ src: asset.url, mediaId: asset.id });
      toast.success('Audio uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="p-2 space-y-3">
      <p className="panel-label">Audio</p>

      {/* Source */}
      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Source URL</label>
        <input
          type="text"
          value={data.src || ''}
          onChange={(e) => update({ src: e.target.value, mediaId: '' })}
          className="input-field text-xs"
          placeholder="https://example.com/audio.mp3"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files?.[0])}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="btn-secondary text-xs mt-1 w-full flex items-center justify-center gap-1 disabled:opacity-50"
        >
          {uploading
            ? <><Loader2 size={11} className="animate-spin" /> Uploading…</>
            : <><Upload size={11} /> Upload audio file</>}
        </button>
      </div>

      {/* Playback toggles */}
      <div className="space-y-1.5">
        <p className="text-xs font-medium" style={{ color: 'var(--t-muted)' }}>Playback</p>
        {[
          { key: 'autoplay', label: 'Autoplay' },
          { key: 'loop',     label: 'Loop' },
          { key: 'showIcon', label: 'Show icon on canvas' },
        ].map(({ key, label }) => (
          <label key={key} className="flex items-center justify-between cursor-pointer">
            <span className="text-xs" style={{ color: 'var(--t-text)' }}>{label}</span>
            <div
              onClick={() => update({ [key]: !data[key] })}
              className="relative w-8 h-4 rounded-full transition-colors cursor-pointer shrink-0"
              style={{ background: data[key] ? 'var(--brand-primary)' : 'var(--t-border2)' }}
            >
              <div
                className="absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform"
                style={{ left: data[key] ? '18px' : '2px' }}
              />
            </div>
          </label>
        ))}
      </div>

      {/* Volume */}
      <div>
        <div className="flex items-center justify-between mb-0.5">
          <label className="text-xs" style={{ color: 'var(--t-muted)' }}>Volume</label>
          <span className="text-xs" style={{ color: 'var(--t-muted)' }}>
            {Math.round((data.volume ?? 1) * 100)}%
          </span>
        </div>
        <input
          type="range"
          min={0} max={1} step={0.01}
          value={data.volume ?? 1}
          onChange={(e) => update({ volume: parseFloat(e.target.value) })}
          className="w-full accent-indigo-500"
        />
      </div>
    </div>
  );
}
